import { FieldWrapper } from "@progress/kendo-react-form";
import {
  Checkbox,
  CheckboxChangeEvent,
  RadioButton,
  RadioButtonChangeEvent,
} from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import { ListView, ListViewItemProps } from "@progress/kendo-react-listview";
import { AxiosError } from "axios";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Else, If, Then } from "react-if";
import { TlxHttpResponse } from "@/api";
import Errors from "@/components/Dialog/Error";
import type { SelectData } from "./FormDropDownList";
import FormLabel from "./FormLabel";
import IconHint from "./IconHint";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormListProps {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  data?: SelectData[];
  selectionMode?: "single" | "multiple";
  loading?: boolean;
  apiError?: AxiosError<TlxHttpResponse>;
}

const ListItemView = ({
  dataItem,
  onChange,
  selectionMode,
}: ListViewItemProps & {
  selectionMode?: "single" | "multiple";
  onChange: (event: boolean, item: SelectData) => void;
}) => {
  const checked = dataItem.checked;
  const name = dataItem.name;

  const onItemClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    onChange(!checked, dataItem);
  };

  const onRadioChangeEvent = (event: RadioButtonChangeEvent) => {
    onChange(event.syntheticEvent.currentTarget.checked, dataItem);
  };

  const onCheckboxChangeEvent = (event: CheckboxChangeEvent) => {
    onChange(event.syntheticEvent.currentTarget.checked, dataItem);
  };

  return (
    <div
      className={`k-list-item ${checked ? "k-state-selected" : ""}`}
      onClick={onItemClick}
    >
      <span className="k-list-item-input">
        <If condition={selectionMode === "single"}>
          <Then>
            <RadioButton checked={checked} onChange={onRadioChangeEvent} />
          </Then>
          <Else>
            <Checkbox checked={checked} onChange={onCheckboxChangeEvent} />
          </Else>
        </If>
      </span>
      <span className="k-list-item-text">{name}</span>
    </div>
  );
};

export const FormList: React.FC<FormListProps> = ({
  name,
  validator,
  label,
  hint,
  hintIcon = false,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  placeholder,
  disabled,
  data,
  selectionMode,
  loading,
  apiError,
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showApiError = !loading && apiError;
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const hintId = showHint ? `${name}_hint` : "";
  const errorId = showError ? `${name}_error` : "";
  const labelId = label ? `${name}_label` : "";

  const [listData, setListData] = useState<SelectData[]>(data ?? []);

  useEffect(() => {
    const source = data ?? [];
    const selectedIds = new Set<string>();
    const value = field.value;
    if (
      selectionMode === "single" &&
      value &&
      typeof value === "object" &&
      "id" in value
    ) {
      selectedIds.add(JSON.stringify((value as SelectData).id));
    } else if (selectionMode === "multiple" && Array.isArray(value)) {
      (value as SelectData[]).forEach(v => {
        if (v?.id !== undefined) selectedIds.add(JSON.stringify(v.id));
      });
    }
    setListData(
      source.map(item => ({
        ...item,
        checked:
          item.id !== undefined && selectedIds.has(JSON.stringify(item.id)),
      })),
    );
  }, [data, field.value, selectionMode]);

  const onCheckboxChange = useCallback(
    (checked: boolean, dataItem: SelectData) => {
      setListData(prevData => {
        if (selectionMode === "single") {
          const newListData = prevData.map(item =>
            item.id === dataItem.id
              ? { ...item, checked }
              : { ...item, checked: false },
          );
          field.onChange(newListData.find(item => item.checked) ?? null);
          return newListData;
        }
        const newListData = prevData.map(item =>
          item.id === dataItem.id ? { ...item, checked } : item,
        );
        field.onChange(newListData.filter(item => item.checked));
        return newListData;
      });
    },
    [field, selectionMode],
  );

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={name}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={
          field.value && (field.value as { name?: string }).name
            ? String((field.value as { name?: string }).name)
            : undefined
        }
        placeholder={placeholder}
        optional={optional}
      >
        <div className={`k-form-field-wrap ${name}`}>
          <ListView
            data={listData}
            item={props => (
              <ListItemView
                {...props}
                onChange={onCheckboxChange}
                selectionMode={selectionMode}
              />
            )}
            style={{ width: "100%" }}
          />
        </div>
        {showHint && !hintIcon && <Hint id={hintId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} />}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
        {showApiError && <Errors errors={[apiError]} />}
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormList;
